import React from 'react';
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import DataGrid from '../../Component/DataGrid/DataGrid';
import './UserDetails.css';
import { TableColumn } from 'react-data-table-component';
import { IUserDetails, UserDetailsProps, UserDetailsState, ISupportStaffs, IAdmins, IDataRowUsers, TabsUserDetails, TabsUserDetailsNames, TabsUserDetailsWidths } from './Interface';
import CustomMaterialMenu from '../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import { ShowAlert } from '../../Component/ShowAlert';
import UserDetailsService from '../../Services/UserDetailsService';
import UserAddress from "./UserAddress"
import CommonUserDialog from './Common/CommonUserDialog';
import UpdateUserPermissions from '../../Component/Permissions/UpdateUserPermissions';
import ManageRolesAndPermissionsDialog from './Common/ManageRolesAndPermissionsDialog';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../Services/UserAndPermissionService';
import EditUserDetails from './EditUserDetails/EditUserDetails';
import { FaRegCopy } from "react-icons/fa";
import BlockUnblockStatusUpdate from './BlockUnblockStatusUpdate';
import { getTabsList, Status, Texts, UserStatus } from '../../Component/Reusables/Utility';
import { ITabData } from '../../Component/DataGrid/Interface';
import DownloadCsv from '../../Component/DataGrid/Plugins/DownloadCsv';
import { amountFormat } from '../../Component/helper';
import moment from 'moment';

enum StatusAction {
    ACTIVATE = 2,
    BLOCK = 3
}

enum IdentityStatus {
    Verified = "Verified",
    InReview = "In Review"
}

const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
        alert("Copied to clipboard!");
    }).catch(err => console.error("Failed to copy: ", err));
};

class UserDetails extends React.Component<UserDetailsProps, UserDetailsState> {
    static contextType = SyncContext;
    public title = "User Details";
    context!: SyncContextType;
    private tableColumns: TableColumn<IUserDetails>[] = [
        {
            name: 'User ID',
            selector: row => row.user_id,
            sortable: false,
            omit: true,
            hide: 1
        },
        {
            name: 'First Name',
            selector: row => row.firstName,
            sortable: true,
            id: "firstName",
            minWidth: "170px",
            wrap: true
        },
        {
            name: 'Last Name',
            selector: row => row.lastName,
            sortable: true,
            id: "lastName",
            minWidth: "170px",
            wrap: true
        },
        {
            name: 'DOB',
            selector: row => row.dob,
            sortable: true,
            id: "dob",
            minWidth: "120px",
            wrap: true,
            center: true
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            id: "email",
            minWidth: "250px",
            wrap: true,
            cell: (row) => (
                <a
                    href={`https://admin.us-east-1-main.seon.io/transactions?searchTerm=${encodeURIComponent(row.email)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {row.email}
                </a>
            )
        },
        {
            name: 'Mobile Number',
            selector: row => row.mobile_number,
            sortable: true,
            id: "mobile_number",
            minWidth: "150px",
            wrap: true,
            compact: true//compact is used to identify whether it is mobile field or not in export excel
            //use compact to prevent auto calculation of + and - in excel
        },
        {
            name: 'Transaction Count',
            selector: row => row.transactionCount,
            sortable: true,
            id: "transactionCount",
            minWidth: "120px",
            right: true,
            omit: true
        },
        {
            name: 'Total Amount (USD)',
            selector: row => amountFormat(row.totalAmount, 'en-US', 'USD'),
            sortable: true,
            id: "totalAmount",
            minWidth: "120px",
            right: true,
            omit: true
        },
        {
            name: 'Last transaction date',
            selector: row => row.lastTransactionDate ? moment(row.lastTransactionDate).format('MM-DD-YYYY hh:mm A') : '',
            sortable: true,
            id: "lastTransactionDate",
            minWidth: "170px",
            center: true,
            omit: true
        },

        {
            name: 'Status',
            selector: row => this.getUserStatus(row.user_status_id),
            sortable: true,
            id: "user_status_id",
            cell: (row) => {
                const status = this.getUserStatus(row.user_status_id);
                return <span className={`status-label ${status.toLowerCase()}`}>{status}</span>;
            },
            wrap: true,
            center: true
        },
        {
            name: 'Block/Unblock Comments',
            selector: row => row.admin_comment ? row.admin_comment : '',
            sortable: true,
            id: "admin_comment",
            minWidth: "150px",
            wrap: true
        },
        {
            name: 'Identity Status',
            selector: row => row.isVerificationRequired === 0 ? IdentityStatus.Verified : IdentityStatus.InReview,
            sortable: true,
            id: "isVerificationRequired",
            cell: (row) => {
                const statusLabel = row.isVerificationRequired === 0 ?
                    <span className='status-label active'>✔ {IdentityStatus.Verified}</span> :
                    <span className="status-label in-review">{IdentityStatus.InReview}</span>;
                return statusLabel;
            },
            wrap: true,
            center: true,
            minWidth: "150px",
        },
        {
            name: 'Persona Id',
            selector: row => row.personaId ? row.personaId : '-',
            sortable: true,
            id: "personaId",
            cell: (row) => (
                row.personaId ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <a
                            href={`https://app.withpersona.com/dashboard/inquiries/${row.personaId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {row.personaId}
                        </a>
                        <FaRegCopy
                            style={{ cursor: "pointer", color: "#007bff" }}
                            onClick={() => copyToClipboard(row.personaId)}
                            title="Copy to clipboard"
                        />
                    </div>
                ) : <span>-</span>
            ),
            minWidth: "300px",
            maxWidth: "300px",
            center: true,
            wrap: true
        },
        {
            name: 'Actions',
            cell: (row) => {
                const { userDetails, transactionLogs } = this.state
                const isAdmin = false //API dev in progress – The boolean needs to be updated based on each user's type.
                const menuItems = [
                    ...(isAdmin
                        ? []
                        : userDetails?.isBlockUserVisible && row.user_status_id === 2
                            ? [{ key: "blocked", label: "Block User" }]
                            : []
                    ),
                    ...(userDetails?.isBlockUserVisible && row.user_status_id === 3
                        ? [{ key: "activated", label: "Activate User" }]
                        : []),
                    ...(userDetails?.isEditUserInfoVisible
                        ? [{ key: "edit_user_details", label: "View/Verify Identity" }]
                        : []),
                    ...(userDetails?.isUserSettingsVisible
                        ? [{ key: "settings_and_history", label: "Settings & Logs" }]
                        : []),
                ];

                if (menuItems.length === 0) {
                    return null;
                }

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string, label_: string) => {
                            if (key_ === "blocked") {
                                this.setState({
                                    selectedUserData: row,
                                    blockStatus: StatusAction.BLOCK,
                                    statusName: key_,
                                    isOpenAdminComments: true
                                });
                            } else if (key_ === "activated") {
                                this.setState({
                                    selectedUserData: row,
                                    blockStatus: StatusAction.ACTIVATE,
                                    statusName: key_,
                                    isOpenAdminComments: true
                                });
                            } else if (key_ === "edit_user_details") {
                                this.setState({
                                    selectedUserData: row,
                                    isOpenEditUserDetailsDialog: true,
                                    userId: row.user_id
                                });
                            } else if (key_ === "settings_and_history") {
                                this.setState({
                                    selectedUserData: row,
                                    isCommonDialogOpen: true,
                                    userId: row.user_id
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ]

    private tableColumnsSupportStaff: TableColumn<ISupportStaffs>[] = [

        {
            name: 'First Name',
            selector: row => row.first_name,
            sortable: true,
            id: "first_name",
            minWidth: "180px",
            wrap: true
        },
        {
            name: 'Last Name',
            selector: row => row.last_name,
            sortable: true,
            id: "last_name",
            minWidth: "180px",
            wrap: true
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            id: "email",
            minWidth: "250px",
            wrap: true
        },
        {
            name: 'Current Role',
            selector: row => row.roleName,
            sortable: true,
            id: "roleName",
            minWidth: "180px",
            maxWidth: "200px",
            wrap: true
        },
        {
            name: 'Status',
            selector: row => this.getUserStatus(row.user_status_id),
            sortable: true,
            id: "user_status_id",
            cell: (row) => {
                const status = this.getUserStatus(row.user_status_id);
                return <span className={`status-label ${status.toLowerCase()}`}>{status}</span>;
            },
            width: '120px',
            center: true
        },
        {
            name: 'Actions',
            cell: (row) => {
                const { userDetails, transactionLogs } = this.state

                const menuItems = [
                    ...(userDetails?.isBlockUserVisible && row.user_status_id === 2
                        ? [{ key: "blocked", label: "Block User" }]
                        : []),
                    ...(userDetails?.isBlockUserVisible && row.user_status_id === 3
                        ? [{ key: "activated", label: "Activate User" }]
                        : []),
                    ...(userDetails?.isUpdatePermissionVisible
                        ? [{ key: "update_permission", label: "Update Role" }]
                        : []),
                    ...(transactionLogs?.isVisible
                        ? [{ key: "transaction_log", label: "Transaction Logs" }]
                        : []),
                ];

                if (menuItems.length === 0) {
                    return null;
                }

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string, label_: string) => {
                            if (key_ === "blocked") {
                                this.UpdateSupportStaffStatus(row, key_, 3);
                            } else if (key_ === "activated") {
                                this.UpdateSupportStaffStatus(row, key_, 2);
                            } else if (key_ === "update_permission") {
                                this.setState({
                                    selectedSupportStaff: row,
                                    isOpenUpdatePermission: true,
                                });
                            } else if (key_ === "transaction_log") {
                                window.location.href = `/transactionlogs?id=${row.email}`;
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ]

    private tableColumnsAdmins: TableColumn<IAdmins>[] = [
        {
            name: 'First Name',
            selector: row => row.first_name,
            sortable: true,
            id: "first_name",
            minWidth: "180px",
            wrap: true
        },
        {
            name: 'Last Name',
            selector: row => row.last_name,
            sortable: true,
            id: "last_name",
            minWidth: "180px",
            wrap: true
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            id: "email",
            minWidth: "250px",
            wrap: true
        },
        {
            name: 'Status',
            selector: row => this.getUserStatus(row.user_status_id),
            sortable: true,
            id: "user_status_id",
            cell: (row) => {
                const status = this.getUserStatus(row.user_status_id);
                return <span className={`status-label ${status.toLowerCase()}`}>{status}</span>;
            },
            width: '120px',
            center: true
        },
        {
            name: 'Actions',
            cell: (row) => {
                const { userDetails, transactionLogs } = this.state

                const menuItems = [
                    ...(userDetails?.isUserSettingsVisible
                        ? [{ key: "settings_and_history", label: "Settings & Logs" }]
                        : []),
                ];

                if (menuItems.length === 0) {
                    return null;
                }

                return (
                    <CustomMaterialMenu
                        fontSize="small"
                        multiPopoverItems={menuItems}
                        onPopoverClick={(key_: string, label_: string) => {
                            if (key_ === "settings_and_history") {
                                this.setState({
                                    selectedAdminData: row,
                                    isCommonDialogOpen: true,
                                    userId: row.user_id
                                });
                            }
                        }}
                        disabled={false}
                    />
                );
            },
            allowOverflow: true,
            button: true,
            width: '120px'
        }
    ]
    private _tabs = Object.values(TabsUserDetails) as TabsUserDetails[];

    constructor(props_: UserDetailsProps) {
        super(props_);
        let URLParams = new URLSearchParams(window.location.search);
        let email_id = URLParams.get("email");
        let tabData: ITabData[] = getTabsList(this._tabs, TabsUserDetails, TabsUserDetailsNames, TabsUserDetailsWidths);
        this.state = {
            accessToken: "",
            userData: [],
            csvData: undefined,
            allSupportStaffs: [],
            allAdmins: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 100,
            searchText: "",
            isCommonDialogOpen: false,
            userId: '',
            isStatusHistory: false,
            selectedUserData: {} as IUserDetails,
            isLoginHistory: false,
            selectedTab: TabsUserDetails.MobileUsers,
            isOpenAddUserDialog: false,
            isOpenUpdatePermission: false,
            selectedSupportStaff: {} as ISupportStaffs,
            userDetails: {
                isVisible: true,
                isVisibleAdminsTab: true,
                isVisibleMobileUsersTab: true,
                isVisibleSupportStaffsTab: true,
                isBlockUserVisible: true,
                isUserSettingsVisible: true,
                isUserSettingSaveBtnVisible: true,
                isStatusHistoryVisible: true,
                isWalletHistoryTabVisible: true,
                isUpdatePermissionVisible: true,
                isDownloadVisibleAdmins: true,
                isDownloadVisibleMobileUsers: true,
                isDownloadVisibleSupportStaffs: true,
                isEditUserInfoVisible: true,
                isAddUserVisible: true
            },
            transactionLogs: {
                isVisible: true,
                isDownloadVisible: true,
                isFraudMarkVisible: true,
                isRefundVisible: true
            },
            selectedAdminData: {} as IAdmins,
            isOpenEditUserDetailsDialog: false,
            email_id: email_id?.toString() || "",
            paginationReset: false,
            isOpenAdminComments: false,
            blockStatus: StatusAction.ACTIVATE,
            statusName: "",
            tabData
        }
    }


    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.userDetails?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const userDetails = permissionResponse.permissions.userDetails
                        const transactionLogs = permissionResponse.permissions.transactionLogs

                        let selectedTab = -1;
                        let tabData = this.state.tabData;
                        for (let tab of this._tabs) {
                            if (userDetails[`${Texts.isVisible}${TabsUserDetails[tab]}Tab`] && selectedTab === -1) {
                                selectedTab = tab;
                            }
                            let isVisible = userDetails[`${Texts.isVisible}${TabsUserDetails[tab]}Tab`];
                            let isDownloadVisible = userDetails[`${Texts.isDownloadVisible}${TabsUserDetails[tab]}`];
                            tabData[tab].visible = isVisible;
                            tabData[tab].isDownloadVisible = isDownloadVisible;
                        }
                        this.setState({ userDetails, selectedTab, transactionLogs, tabData }, () => {
                            this.fetchData()
                        });

                    } else {
                        this.fetchData()
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    onClickAddUserButton = () => {
        this.setState({ isOpenAddUserDialog: true })
    };

    handleTabChange = (event: React.ChangeEvent<{}>, newValue: TabsUserDetails) => {
        // Create a new URLSearchParams object from the current URL
        const URLParams = new URLSearchParams(window.location.search);

        URLParams.delete("email");

        // Update the URL without reloading the page
        const newUrl = `${window.location.pathname}${URLParams.toString()}`;
        window.history.replaceState(null, "", newUrl);
        this.setState(prevState => ({
            selectedTab: newValue,
            searchText: "",
            email_id: "",
            currentPage: 1,
            paginationReset: !prevState.paginationReset,
            csvData: undefined
        }), () => this.fetchData());
    };

    fetchData = () => {
        switch (this.state.selectedTab) {
            case 0:
                this.getUserDetails();
                break;
            case 1:
                this.getAllSupportStaffs();
                break;
            case 2:
                this.getAllAdmins();
                break;
            default:
                break;
        }
    };

    getTableData = () => {
        switch (this.state.selectedTab) {
            case TabsUserDetails.MobileUsers:
                return this.state.userData; // Mobile Users
            case TabsUserDetails.SupportStaffs:
                return this.state.allSupportStaffs; // Support Staffs
            case TabsUserDetails.Admins:
                return this.state.allAdmins; // Admins
            default:
                return [];
        }
    };

    getTableColumns = () => {
        switch (this.state.selectedTab) {
            case TabsUserDetails.MobileUsers:
                return this.tableColumns as TableColumn<IDataRowUsers>[];
            case TabsUserDetails.SupportStaffs:
                return this.tableColumnsSupportStaff as TableColumn<IDataRowUsers>[];
            case TabsUserDetails.Admins:
                return this.tableColumnsAdmins as TableColumn<IDataRowUsers>[];
            default:
                return [];
        }
    }

    updateData = () => {
        this.fetchData();
    };


    public isDownloadVisible(): boolean | undefined {
        return this.state.userDetails ? (this.state.userDetails as any)[`${Texts.isDownloadVisible}${TabsUserDetails[this.state.selectedTab]}`] : false;
    };

    public prepareCSVCustom(objDownloadCSV: DownloadCsv<IDataRowUsers>) {
        let _this = objDownloadCSV.props.parentComponent as UserDetails;
        switch (_this.state.selectedTab) {
            case TabsUserDetails.MobileUsers:
                _this.getUserDetails(objDownloadCSV);
                break;
            default:
                objDownloadCSV.prepareCSV();
        }
    }

    public render() {
        const { userDetails, searchText, totalRows, selectedTab, transactionLogs, paginationReset, csvData } = this.state
        const {
            isStatusHistoryVisible, isAddUserVisible, isWalletHistoryTabVisible
        } = userDetails;

        const { isVisible } = transactionLogs;

        return (
            <div className="body-sub-container table-container">
                {selectedTab !== null && (
                    <DataGrid
                        title={this.title}
                        search={this.title}
                        defaultSearch={this.state.email_id || searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                email_id: searchText_,
                                currentPage: 1
                            }, () => {
                                this.fetchData();
                            });
                        }}
                        columns={this.getTableColumns()}
                        data={this.getTableData()}
                        csvData={csvData}
                        onChangePage={(page: number) => {
                            this.setState({ currentPage: page }, () => {
                                this.fetchData();
                            });
                        }}
                        onChangeRowsPerPage={(rowsPerPage_: number, currentPage_: number) => {
                            this.setState({ currentRowsPerPage: rowsPerPage_, currentPage: currentPage_ }, () => {
                                this.fetchData();
                            });
                        }}
                        expandableRows={selectedTab === TabsUserDetails.MobileUsers} // Enable expandable rows for Mobile Users
                        expandedComponent={(data_: any) =>
                            selectedTab === TabsUserDetails.MobileUsers ? <UserAddress data={[data_.data]} /> : <></>
                        }
                        pagination
                        paginationTotalRows={totalRows}
                        exportCsv={this.isDownloadVisible()}
                        prepareCSVCustom={this.prepareCSVCustom}
                        parentComponent={this}
                        selectedTab={selectedTab}
                        csvFileName={`${this.title} - ${(TabsUserDetailsNames as any)[TabsUserDetails[selectedTab]]}`}
                        onTabChange={this.handleTabChange}
                        onClickAddUserButton={this.onClickAddUserButton}
                        showAddUserButton={selectedTab === TabsUserDetails.Admins && isAddUserVisible}
                        titleAddUserButton='Manage Staffs'
                        paginationDefaultPage={1}
                        paginationResetDefaultPage={paginationReset}
                        tabData={this.state.tabData}
                    />
                )}
                {
                    this.state.isCommonDialogOpen && <CommonUserDialog open={this.state.isCommonDialogOpen}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isCommonDialogOpen: false
                            })
                        }}
                        userId={this.state.userId}
                        selectedUserData={this.state.selectedUserData}
                        selectedAdminData={this.state.selectedAdminData}
                        accessToken={this.state.accessToken}
                        isStatusHistoryVisible={isStatusHistoryVisible}
                        isTransactionLogsVisible={isVisible}
                        isWalletHistoryTabVisible={isWalletHistoryTabVisible}
                        selectedTab={selectedTab}
                    />
                }
                {this.state.isOpenAddUserDialog &&
                    <ManageRolesAndPermissionsDialog
                        open={this.state.isOpenAddUserDialog}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenAddUserDialog: false
                            })
                        }}
                        accessToken={this.state.accessToken}
                    />
                }
                {this.state.isOpenUpdatePermission &&
                    <UpdateUserPermissions
                        open={this.state.isOpenUpdatePermission}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenUpdatePermission: false
                            })
                        }}
                        selectedSupportStaff={this.state.selectedSupportStaff}
                        accessToken={this.state.accessToken}
                        refreshData={this.updateData}
                    />
                }
                {this.state.isOpenEditUserDetailsDialog &&
                    <EditUserDetails
                        open={this.state.isOpenEditUserDetailsDialog}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenEditUserDetailsDialog: false,
                            })
                        }}
                        selectedUserData={this.state.selectedUserData}
                        accessToken={this.state.accessToken}
                        refreshData={this.updateData}
                    />
                }
                {this.state.isOpenAdminComments &&
                    <BlockUnblockStatusUpdate
                        open={this.state.isOpenAdminComments}
                        onClose={(state: boolean, action: string) => {
                            this.setState({
                                isOpenAdminComments: false
                            })
                        }}
                        selectedData={this.state.selectedUserData}
                        accessToken={this.state.accessToken}
                        refreshData={this.fetchData}
                        stausName={this.state.statusName}
                        statusCode={this.state.blockStatus}
                    />
                }
            </div>
        )
    }


    private getUserStatus(statusId: number) {
        return statusId === UserStatus.ACTIVE ? Status.ACTIVE :
            statusId === UserStatus.BLOCKED ? Status.BLOCKED :
                Status.UNKNOWN;
    }


    private getUserDetails(objDownloadCSV?: DownloadCsv<IDataRowUsers>) {
        let pageIndex: number = this.state.currentPage;
        let reqParams: any = {};
        if (!objDownloadCSV) {
            reqParams = {
                "pageNumber": pageIndex,
                "recordPerPage": this.state.currentRowsPerPage
            };
        }

        if (this.state.searchText || this.state.email_id) {
            reqParams["searchText"] = this.state.searchText || this.state.email_id;
        } else {
            const URLParams = new URLSearchParams(window.location.search);

            // Remove the "email" parameter from the URL
            URLParams.delete("email");
            const newUrl = `${window.location.pathname}${URLParams.toString()}`;
            window.history.replaceState(null, "", newUrl);
        }

        UserDetailsService.getUserDetails(this.state.accessToken, reqParams).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    if (objDownloadCSV) {
                        let csvData = resp.userDetailsList
                        if (csvData.length >= 1) {
                            let _this = objDownloadCSV.props.parentComponent as UserDetails;
                            _this.setState({
                                csvData: csvData
                            }, () => {
                                objDownloadCSV.prepareCSV();
                            })
                        } else {
                            objDownloadCSV.errormsg();
                        }
                    }
                    else {
                        this.setState({
                            userData: resp.userDetailsList,
                            totalRows: resp.pageCount
                        })
                    }
                });
            }
        });
    }

    private getAllSupportStaffs() {
        let pageIndex: number = this.state.currentPage;
        let pagination: any = {
            "pageNumber": pageIndex,
            "recordsPerPage": this.state.currentRowsPerPage
        }

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText
        }

        UserDetailsService.getAllSupportStaffs(this.state.accessToken, pagination).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        allSupportStaffs: resp.supportStaffList,
                        totalRows: resp.pageCount
                    })
                });
            }
        });
    }

    private getAllAdmins() {
        let pageIndex: number = this.state.currentPage;
        let pagination: any = {
            "pageNumber": pageIndex,
            "recordsPerPage": this.state.currentRowsPerPage
        }

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText
        }

        UserDetailsService.getAllAdmins(this.state.accessToken, pagination).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    this.setState({
                        allAdmins: resp.adminList,
                        totalRows: resp.pageCount
                    })
                });
            }
        });
    }

    // Suppot staffs...
    private async UpdateSupportStaffStatus(row_: ISupportStaffs, key_: string, status: number) {
        let postdata: any = {
            "userId": row_.user_id,
            "userStatus": status
        }

        let resp = await UserDetailsService.UpdateSupportStaffStatus(this.state.accessToken, row_?.user_id, status);

        if (resp.ok) {
            ShowAlert("PassportPay", `The user has been marked as ${key_}.`, 'success').then(() => {
                this.fetchData();
            });
        }
    }
}

export default UserDetails;